import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { api, fileUrl, formatApiErrorDetail } from "../lib/gmao";
import { useFileSrc } from "../lib/useFileSrc";
import { UploadSimple, X, SpinnerGap } from "@phosphor-icons/react";

export function Pill({ label, color, className = "", ...rest }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide border ${className}`}
      style={{ borderColor: color, color }} {...rest}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

export function Btn({ children, variant = "primary", className = "", ...props }) {
  const v = {
    primary: "bg-blue-600 text-white hover:bg-blue-700",
    secondary: "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50",
    technical: "bg-slate-900 text-white hover:bg-slate-800 font-mono text-sm uppercase tracking-wide",
    danger: "bg-white border border-red-300 text-red-600 hover:bg-red-50",
  }[variant];
  return (
    <button className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm font-medium text-sm transition-all duration-150 ease-out disabled:opacity-50 ${v} ${className}`} {...props}>
      {children}
    </button>
  );
}

// Etiqueta con «*» = campo obligatorio: el control hijo recibe `required` y, mientras esté vacío o sea inválido,
// se muestra en amarillo (#FFFFCC); con valor válido pasa a blanco (regla FOR DECI). Los demás campos van en blanco.
export function Field({ label, children }) {
  const required = typeof label === "string" && /\*\s*$/.test(label);
  const child = required && React.isValidElement(children) && children.props.required === undefined
    ? React.cloneElement(children, { required: true })
    : children;
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-[0.12em] text-slate-500 mb-1.5">{label}</span>
      {child}
    </label>
  );
}

export function Input({ className = "", ...props }) {
  return <input {...props} className={`w-full px-3 py-2 bg-white border border-slate-300 rounded-sm text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all ${className}`} />;
}

export function Textarea({ className = "", ...props }) {
  return <textarea {...props} className={`w-full px-3 py-2 bg-white border border-slate-300 rounded-sm text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all ${className}`} />;
}

export function Select({ className = "", ...props }) {
  return <select {...props} className={`w-full px-3 py-2 bg-white border border-slate-300 rounded-sm text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all ${className}`} />;
}

// Pila de modales abiertos: Escape solo cierra el de más arriba (desplegable/ventana secundaria antes que el editor).
const modalStack = [];
export function Modal({ open, onClose, title, children, wide, size, fullscreen, fullWidth }) {
  const idRef = useRef(Symbol("modal"));
  useEffect(() => {
    if (!open) return;
    const id = idRef.current;
    modalStack.push(id);
    const onKey = (e) => {
      if (e.key !== "Escape" || modalStack[modalStack.length - 1] !== id) return;
      const t = e.target;
      if (t && (t.tagName === "SELECT" || (t.tagName === "INPUT" && t.getAttribute("list")))) { t.blur(); return; }  // primero se cierra el desplegable activo
      onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); const k = modalStack.indexOf(id); if (k >= 0) modalStack.splice(k, 1); };
  }, [open, onClose]);
  if (!open) return null;
  let maxW = "max-w-lg";
  if (size === "xl") maxW = "max-w-5xl";
  else if (wide) maxW = "max-w-3xl";
  if (fullscreen) {
    return createPortal(
      <div className="fixed inset-0 z-50 flex flex-col bg-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0 bg-white">
          <h3 className="font-head text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-slate-400 hover:text-slate-900"><X size={22} /></button>
        </div>
        <div className="p-6 sm:p-8 overflow-y-auto flex-1">
          <div className={fullWidth ? "w-full" : "max-w-4xl mx-auto"}>{children}</div>
        </div>
      </div>,
      document.body
    );
  }
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-6 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-white sm:border border-slate-800 shadow-2xl w-full ${maxW} h-full sm:h-auto sm:max-h-[92vh] flex flex-col fade-up`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0 bg-white">
          <h3 className="font-head text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>,
    document.body
  );
}

const IMG_EXT_RE = /\.(jpe?g|png|webp|gif|heic|heif|bmp)$/i;
const MAX_IMG_MB = 15;
const MAX_FILE_MB = 100;

export function FileUpload({ value, onChange, onChangeMany, accept = "image/*", label = "Subir archivo", testid, capture, multiple = false }) {
  const ref = useRef();
  const previewSrc = useFileSrc(value);
  const lastFiles = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [drag, setDrag] = useState(false);
  const isImg = accept.includes("image") && !accept.includes("video");
  const isVideo = accept.includes("video");
  const allowPdf = accept.includes("pdf");

  const validate = (f) => {
    if (isVideo) {
      if (!f.type.startsWith("video/")) return "Solo se admiten vídeos.";
      if (f.size > MAX_FILE_MB * 1024 * 1024) return `El vídeo supera el máximo de ${MAX_FILE_MB} MB.`;
      return null;
    }
    const isPdf = allowPdf && (f.type === "application/pdf" || /\.pdf$/i.test(f.name));
    if (isImg && !isPdf) {
      if (!(f.type.startsWith("image/") || IMG_EXT_RE.test(f.name)))
        return allowPdf ? "Solo se admiten imágenes (JPG, PNG, WEBP, HEIC) o PDF." : "Solo se admiten imágenes (JPG, PNG, WEBP o HEIC).";
      if (f.size > MAX_IMG_MB * 1024 * 1024) return `La imagen supera el máximo de ${MAX_IMG_MB} MB.`;
    } else if (f.size > MAX_FILE_MB * 1024 * 1024) {
      return `El archivo supera el máximo de ${MAX_FILE_MB} MB.`;
    }
    return null;
  };

  const doUpload = async (fileList) => {
    if (busy) return;
    const files = multiple ? Array.from(fileList || []) : Array.from(fileList || []).slice(0, 1);
    if (!files.length) return;
    for (const f of files) {
      const err = validate(f);
      if (err) { setError(err); return; }
    }
    lastFiles.current = files;
    setError(null); setBusy(true); setProgress(0);
    const ids = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append("file", files[i]);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const frac = e.total ? e.loaded / e.total : 0;
            setProgress(Math.min(100, Math.round(((i + frac) / files.length) * 100)));
          },
        });
        ids.push(data.id);
      }
      lastFiles.current = null;
      if (multiple && onChangeMany) onChangeMany(ids);
      else onChange(ids[0]);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "No se pudo subir el archivo. Inténtalo de nuevo.");
    } finally { setBusy(false); }
  };

  const dz = {
    onDragOver: (e) => { if (e.dataTransfer?.types?.includes("Files")) { e.preventDefault(); setDrag(true); } },
    onDragLeave: () => setDrag(false),
    onDrop: (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault(); setDrag(false);
      if (e.dataTransfer.files?.length) doUpload(e.dataTransfer.files);
    },
  };

  const dropHint = isImg ? "Suelta aquí la fotografía" : isVideo ? "Suelta aquí el vídeo" : "Suelta aquí el archivo";
  return (
    <div className="inline-block max-w-full" data-testid={testid ? `${testid}-dropzone` : undefined} {...dz}>
      <input ref={ref} type="file" accept={accept} multiple={multiple} {...(capture ? { capture } : {})} className="hidden"
        onChange={(e) => { doUpload(e.target.files); e.target.value = ""; }} data-testid={testid} />
      {busy ? (
        <div className="w-44 px-3 py-2 border border-dashed border-blue-400 rounded-sm bg-blue-50/50" data-testid={testid ? `${testid}-progress` : undefined}>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-150" style={{ width: `${progress}%` }} />
          </div>
          <div className="text-xs text-slate-600 mt-1.5 flex items-center gap-1.5">
            <SpinnerGap size={12} className="animate-spin" /> Subiendo… {progress}%
          </div>
        </div>
      ) : value && isImg ? (
        <div className={`relative inline-block ${drag ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}>
          <img src={previewSrc} alt="preview" className="w-24 h-24 object-cover border border-slate-300" />
          {drag && (
            <div className="absolute inset-0 bg-blue-600/75 flex items-center justify-center text-center text-[10px] font-semibold text-white p-1 leading-tight">
              {dropHint}
            </div>
          )}
          <button type="button" onClick={() => onChange(null)} data-testid={testid ? `${testid}-remove` : undefined}
            className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-0.5"><X size={14} /></button>
        </div>
      ) : value && isVideo ? (
        <div className="relative inline-block">
          <video src={fileUrl(value)} controls className="w-40 h-24 object-cover border border-slate-300 bg-black" />
          <button type="button" onClick={() => onChange(null)} className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-0.5"><X size={14} /></button>
        </div>
      ) : (
        <div>
          <button type="button" onClick={() => ref.current.click()}
            className={`inline-flex items-center gap-2 px-3 py-2 border border-dashed rounded-sm text-sm transition-colors duration-150 ${
              drag ? "border-blue-600 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300 text-slate-600 hover:border-blue-600 hover:text-blue-600"}`}>
            <UploadSimple size={16} />
            {drag ? dropHint : value ? "Archivo subido ✓" : label}
          </button>
          {error && (
            <div className="mt-1.5 text-xs text-red-600 flex items-center gap-2 flex-wrap" data-testid={testid ? `${testid}-error` : undefined}>
              <span>{error}</span>
              {lastFiles.current && (
                <button type="button" onClick={() => doUpload(lastFiles.current)} className="underline font-semibold" data-testid={testid ? `${testid}-retry` : undefined}>
                  Reintentar
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
